import { Navigate, RouterProvider, createBrowserRouter } from "react-router-dom";

import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/lib/auth";
import { AccessDenied } from "@/pages/AccessDenied";
import { Login } from "@/pages/Login";
import { Results } from "@/pages/Results";
import { Search } from "@/pages/Search";

/**
 * Top-level routing. `/login` and `/access-denied` are standalone; every
 * other route renders inside the AppShell (which itself wraps children
 * in the AllowlistGate). The root "/" redirects to "/search".
 *
 * AuthRedirect is the thin element that bounces unauthenticated users to
 * /login; once signed in, the AppShell takes over.
 */
const router = createBrowserRouter([
  { path: "/login", element: <Login /> },
  { path: "/access-denied", element: <AccessDenied /> },
  {
    path: "/",
    element: (
      <AuthRedirect>
        <AppShell />
      </AuthRedirect>
    ),
    children: [
      { index: true, element: <Navigate to="/search" replace /> },
      { path: "search", element: <Search /> },
      { path: "results", element: <Results /> },
      { path: "*", element: <Navigate to="/search" replace /> },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}

function AuthRedirect({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-2 w-32 animate-pulse rounded bg-muted" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}
